// src/components/steps/FeaturesStep.js
import React from 'react';
import {
  VStack,
  Text,
  Box,
  Button,
  SimpleGrid,
  HStack,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import {
  Scale,
  Package,
  Shield,
  Tablet,
  Heart,
  Sparkles,
  User,
} from 'lucide-react';

const featureOptions = [
  {
    value: 'lightweight',
    label: '軽量',
    description: '負担の少ない軽い重さ',
    icon: <Scale size={32} strokeWidth={1.5} />,
    color: 'blue.400'
  },
  {
    value: 'large-capacity',
    label: '大容量',
    description: 'たくさん収納できる',
    icon: <Package size={32} strokeWidth={1.5} />,
    color: 'purple.400'
  },
  {
    value: 'durable',
    label: '頑丈💪',
    description: '長く使える耐久性',
    icon: <Shield size={32} strokeWidth={1.5} />,
    color: 'red.400'
  },
  {
    value: 'small-child',
    label: '小柄なお子様向け',
    description: '体格に合わせた設計',
    icon: <User size={32} strokeWidth={1.5} />,
    color: 'pink.400'
  },
  {
    value: 'tablet-pocket',
    label: 'タブレットポケット',
    description: 'ICT機器の収納に対応',
    icon: <Tablet size={32} strokeWidth={1.5} />,
    color: 'green.400'
  },
  {
    value: 'clarino',
    label: 'クラリーノ',
    description: '軽くて丈夫な人工皮革',
    icon: <Heart size={32} strokeWidth={1.5} />,
    color: 'orange.400'
  },
  {
    value: 'cordovan',
    label: 'コードバン',
    description: '最高級の馬革素材',
    icon: <Sparkles size={32} strokeWidth={1.5} />,
    color: 'purple.600'
  }
];

const FeaturesStep = ({ formData, onChange }) => {
  const handleFeatureToggle = (feature) => {
    const currentFeatures = formData.features || [];
    const newFeatures = currentFeatures.includes(feature)
      ? currentFeatures.filter(f => f !== feature)
      : [...currentFeatures, feature];
    
    onChange({
      target: { name: 'features', value: newFeatures }
    });
  };

  return (
    <VStack spacing={{ base: 6, md: 8 }} align="stretch">
      <Box>
        <Text
          fontSize={{ base: "xl", md: "2xl" }}
          fontWeight="bold"
          textAlign="center"
          bgGradient="linear(to-r, blue.400, green.400)"
          bgClip="text"
        >
          重視したい特徴を選んでください
        </Text>
        <Text 
          fontSize={{ base: "md", md: "md" }}
          color="gray.600" 
          fontWeight="normal" 
          textAlign="center"
          mt={2}
        >
          複数選択可能です
        </Text>
      </Box>

      <SimpleGrid 
        columns={{ base: 1, md: 2 }} 
        spacing={{ base: 3, md: 4 }}
      >
        {featureOptions.map((option) => (
          <Button
            key={option.value}
            onClick={() => handleFeatureToggle(option.value)}
            height="auto"
            p={{ base: 4, md: 4 }}
            bg={
              formData.features?.includes(option.value)
                ? `${option.color}`
                : 'white'
            }
            color={
              formData.features?.includes(option.value)
                ? 'white'
                : 'gray.700'
            }
            _hover={{ 
              transform: 'translateY(-2px)',
              boxShadow: 'md',
              bg: option.color,
              color: 'white',
              transition: 'all 0.2s'
            }}
            border="2px"
            borderColor={option.color}
            borderRadius="xl"
          >
            <HStack spacing={3} width="100%">
              <Box 
                color={
                  formData.features?.includes(option.value)
                    ? 'white'
                    : option.color
                }
                transition="all 0.2s"
              >
                {option.icon}
              </Box>
              <VStack 
                align="start" 
                spacing={0}
                flex="1"
              >
                <Text 
                  fontSize={{ base: "md", md: "lg" }}
                  fontWeight="bold"
                >
                  {option.label}
                </Text>
                <Text 
                  fontSize={{ base: "sm", md: "sm" }}
                  opacity={0.9}
                  color={
                    formData.features?.includes(option.value)
                      ? 'white'
                      : 'gray.600'
                  }
                >
                  {option.description}
                </Text>
              </VStack>
            </HStack>
          </Button>
        ))}
      </SimpleGrid>

      {/* 選択中の特徴表示 */}
      {formData.features?.length > 0 && (
        <Box mt={{ base: 3, md: 4 }}>
          <Text 
            fontSize={{ base: "sm", md: "sm" }}
            color="gray.600" 
            mb={2}
          >
            選択中の特徴:
          </Text>
          <Wrap spacing={2}>
            {formData.features.map((feature) => {
              const option = featureOptions.find(opt => opt.value === feature);
              return (
                <WrapItem key={feature}>
                  <HStack
                    px={3}
                    py={1}
                    bg={option.color}
                    color="white"
                    borderRadius="full"
                    fontSize={{ base: "xs", md: "sm" }}
                  >
                    {option.icon}
                    <Text>{option.label}</Text>
                  </HStack>
                </WrapItem>
              );
            })}
          </Wrap>
        </Box>
      )}

      <Box
        mt={{ base: 4, md: 6 }}
        p={{ base: 4, md: 6 }}
        bg="blue.50"
        borderRadius="xl"
        borderLeft="4px"
        borderColor="blue.400"
      >
        <Text 
          color="blue.600"
          fontSize={{ base: "sm", md: "md" }}
        >
          選択された特徴を重視して、最適なランドセルを見つけるお手伝いをします。
          複数の特徴を組み合わせることで、より細かな条件指定が可能です。
        </Text>
      </Box>
    </VStack>
  );
};

export default FeaturesStep;